import { CollapseAll, ExpandAll } from '@carbon/react/icons';
import {
  ActionableNotification,
  Button,
  Checkbox,
  Dropdown,
  InlineNotification,
  Search,
} from '@carbon/react';
import { DownloadButton } from 'components/download-button';
import { RecalculateWorkspace } from 'components/recalculate-workspace';
import { atom, useAtom, useSetAtom, WritableAtom } from 'jotai';
import { useDownloadResource } from 'lib/hooks/useDownloadFile';
import { debounce } from 'lodash-es';
import { useMemo, useState } from 'react';
import {
  runsheetExpandWithCalculationDetails,
  runsheetSearchPreferencesAtom,
  useRunsheetSearch,
} from './atoms';
import { Runsheet } from './runsheet';
import style from './runsheet-search.module.scss';
import sortFields from './sort-fields';
import { OnPaginationChange } from './types';
import { ProvisionsFilter } from 'components/provision-filter';
import ErrorToast from 'components/error-toast';

const RunsheetSearch = ({
  searchHref,
  runsheetDownloadsHref,
  skipUrlSearchParams = false,
  titleWorkspace,
  referenceLocationHref,
  titleWorkspaceHref,
  calculationHref,
  showEditInterpretationButton = false,
  startingInterestDate,
  runningCalculationChannel,
  runningCalculationHref,
  runningCalculationCancelHref,
}: {
  searchHref?: string;
  runsheetDownloadsHref?: string;
  skipUrlSearchParams?: boolean;
  titleWorkspace?: number;
  referenceLocationHref?: string;
  titleWorkspaceHref?: string;
  calculationHref?: string;
  showEditInterpretationButton?: boolean;
  startingInterestDate?: string | null;
  runningCalculationChannel?: string | null;
  runningCalculationHref?: string;
  runningCalculationCancelHref?: string;
}) => {
  const [searchResults, setSearchPayload] = useRunsheetSearch({
    skipUrlSearchParams,
    searchHref,
  });
  const [isTitleCalculationError, setIsTitleCalculationError] = useState(false);

  // Reconcile deep-links open this filtered to a single entity. The id drives the
  // backend exact filter (via atoms) and prefills the search box; the name is shown
  // in the source-context banner. A lazy initializer, not useMemo: useMemo may
  // recompute, and atoms rewrites the query string once the filter is cleared.
  const [reconcileEntityFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('exactEntityId');
    return id ? { id, name: params.get('exactEntityName') ?? '' } : null;
  });

  const [searchDisplayText, setSearchDisplayText] = useState(
    () =>
      reconcileEntityFilter?.id ??
      new URLSearchParams(window.location.search).get('text') ??
      ''
  );
  const downloadList = useDownloadResource(runsheetDownloadsHref);

  const [preferences, setPreferences] = useAtom(runsheetSearchPreferencesAtom);

  const expanded = preferences.expanded;
  const expandAllRowsAtom = useMemo(() => {
    return atom<{ open: boolean }, [{ open: boolean }], void>(
      () => ({ open: expanded ?? false }),
      (get, set, payload) => {
        setPreferences((current) => ({ ...current, expanded: payload.open }));
      }
    );
  }, [expanded, setPreferences]);

  const debouncedSetSearchPayload = useMemo(
    () =>
      debounce((value) => {
        // Editing the free text drops any reconcile entity filter and reruns a
        // plain text search (INS-11025); undefined keys are stripped from the URL.
        const newAttrs = {
          page: 1,
          text: value,
          exactEntityId: undefined,
        };
        setSearchPayload({
          params: newAttrs,
          urlSearchParams: newAttrs,
          resetSearch: true,
        });
      }, 400),
    [setSearchPayload]
  );

  const onPaginationChange: OnPaginationChange = (newSearch) => {
    setSearchPayload({
      params: newSearch,
      persistenceParams: { pageSize: newSearch.pageSize },
      urlSearchParams: { page: newSearch.page, pageSize: newSearch.pageSize },
    });
  };

  const hasCompletion = searchResults.currentPayload?.hasCompletion;

  // The entity filter currently applied (cleared by "View All" or a text edit).
  const activeEntityFilter = searchResults.currentPayload?.exactEntityId;

  const clearEntityFilter = () => {
    setSearchDisplayText('');
    setSearchPayload({
      params: { page: 1, exactEntityId: undefined },
      urlSearchParams: { exactEntityId: undefined },
      resetSearch: true,
    });
  };

  // Reconcile source-context banner + the "no matching transactions" explanation
  // (workspace runsheet only), shown while an entity filter is active (INS-11025).
  const noMatchingTransactions =
    !!titleWorkspace &&
    !searchResults.loading &&
    searchResults.data?.results?.length === 0;

  const label = sortFields.find(
    (el) =>
      el.id === searchResults.currentPayload?.sortAttribute &&
      el.direction === searchResults.currentPayload?.sortDirection
  )?.text;

  return (
    <>
      {isTitleCalculationError && (
        <ErrorToast message="Calculation failed. Please contact support." />
      )}
      {activeEntityFilter && (
        <ActionableNotification
          inline
          lowContrast
          kind="info"
          hideCloseButton
          title="Opened from Reconcile,"
          subtitle={
            reconcileEntityFilter?.name
              ? `filtered to ${reconcileEntityFilter.name} (Entity ID: ${activeEntityFilter}).`
              : `filtered to Entity ID: ${activeEntityFilter}.`
          }
          actionButtonLabel="View All →"
          onActionButtonClick={clearEntityFilter}
        />
      )}
      {activeEntityFilter && noMatchingTransactions && (
        <InlineNotification
          lowContrast
          kind="info"
          hideCloseButton
          title={`No Runsheet transactions match ${
            reconcileEntityFilter?.name
              ? `${reconcileEntityFilter.name} (Entity ID: ${activeEntityFilter})`
              : `Entity ID: ${activeEntityFilter}`
          } in this Title Workspace`}
          subtitle="This entity is still included in this Title Workspace because of:"
        >
          <ul className={style.inclusionReasons}>
            <li>
              <strong>Starting Interest</strong> — a seeded starting-interest
              entry exists for this entity in this workspace, predating any
              recorded instrument
            </li>
            <li>
              <strong>Events</strong> — no transaction-linked interest event
              names this entity in this workspace
            </li>
            <li>
              <strong>Working Interest Owner</strong> — linked via a well
              intersection calculation in this workspace, not a recorded
              conveyance
            </li>
          </ul>
        </InlineNotification>
      )}
      <div className={style.gridTopRow}>
        <div className={style.searchWrapper}>
          <Search
            id="search-input"
            {...{ autoFocus: true }}
            labelText="Search"
            placeholder="Search by any runsheet attributes"
            // Controlled: "View All" clears this, and an uncontrolled defaultValue
            // would leave the entity id sitting in the box after the filter drops.
            value={searchDisplayText}
            onChange={({ target: { value } }) => {
              setSearchDisplayText(value || '');
              debouncedSetSearchPayload(value || '');
            }}
            size="lg"
          />
        </div>
        <div className={style.hideReviewed}>
          <Checkbox
            id="hide-reviewed"
            labelText="Hide Reviewed"
            checked={searchResults.currentPayload?.hideReviewed ?? false}
            onChange={(_evt, { checked }: { checked: boolean }) => {
              const newAttrs = {
                page: 1,
                hideReviewed: checked,
              };
              setSearchPayload({
                params: newAttrs,
                persistenceParams: {
                  hideReviewed: newAttrs.hideReviewed,
                },
                urlSearchParams: newAttrs,
              });
            }}
          />
        </div>
        <div className={style.expandAllButtons}>
          <ExpandButtons expandedRowsAtom={expandAllRowsAtom} />
        </div>

        <div className={style.sortColumnWrapper}>
          <Dropdown
            id="default"
            titleText=""
            label={label || ''}
            items={sortFields}
            itemToString={(item) => (item ? item.text : '')}
            onChange={({
              selectedItem,
            }: {
              selectedItem: {
                id: string;
                text: string;
                direction: 'ASC' | 'DESC';
              };
            }) => {
              const newAttrs = {
                page: 1,
                sortAttribute: selectedItem.id,
                sortDirection: selectedItem.direction,
              };
              setSearchPayload({
                params: newAttrs,
                persistenceParams: {
                  sortAttribute: selectedItem.id,
                  sortDirection: selectedItem.direction,
                },
                urlSearchParams: newAttrs,
              });
            }}
          />
        </div>
        <div className={style.sortColumnWrapper}>
          <ProvisionsFilter
            hasCompletion={hasCompletion}
            onChange={(hasCompletion) => {
              const newAttrs = {
                page: 1,
                hasCompletion,
              };
              setSearchPayload({
                params: newAttrs,
                persistenceParams: {
                  hasCompletion,
                },
                urlSearchParams: newAttrs,
              });
            }}
          />
        </div>

        {runsheetDownloadsHref && (
          <div className={style.downloadField}>
            <DownloadButton
              loading={downloadList.generatingDownload}
              loadingText="Building List"
              size="md"
              kind="primary"
              onClick={() => downloadList.triggerDownload()}
            >
              Download List
            </DownloadButton>
          </div>
        )}

        {calculationHref && (
          <div className={style.recalculateButton}>
            {/* No onSuccess: the runsheet doesn't consume the recalc result;
                calculation details are fetched per interpretation on row expand. */}
            <RecalculateWorkspace
              runningCalculationChannel={runningCalculationChannel || null}
              runningCalculationHref={runningCalculationHref}
              runningCalculationCancelHref={runningCalculationCancelHref}
              startingInterestDate={startingInterestDate}
              calculationsHref={calculationHref}
              onTitleCalculationError={(isTitleCalculationError) => {
                setIsTitleCalculationError(isTitleCalculationError);
              }}
            />
          </div>
        )}
      </div>
      <Runsheet
        searchAtomValue={searchResults}
        actions={undefined}
        onPaginationChange={onPaginationChange}
        expandedRowsAtom={expandAllRowsAtom}
        titleWorkspace={titleWorkspace}
        referenceLocationHref={referenceLocationHref}
        titleWorkspaceHref={titleWorkspaceHref}
        showEditInterpretationButton={showEditInterpretationButton}
      />
    </>
  );
};

const ExpandButtons = ({
  expandedRowsAtom,
}: {
  expandedRowsAtom: WritableAtom<{ open: boolean }, [{ open: boolean }], void>;
}) => {
  const setPreferences = useSetAtom(expandedRowsAtom);
  const [expandWithCalculationDetails, setExpandWithCalculationDetails] =
    useAtom(runsheetExpandWithCalculationDetails);

  return (
    <>
      <Button
        kind="ghost"
        hasIconOnly
        iconDescription="Expand all"
        size="md"
        onClick={() => {
          setPreferences({ open: true });
        }}
        renderIcon={ExpandAll}
        tooltipAlignment="center"
        tooltipPosition="bottom"
      />
      <Button
        kind="ghost"
        hasIconOnly
        iconDescription="Collapse all"
        size="md"
        onClick={() => {
          setPreferences({ open: false });
        }}
        renderIcon={CollapseAll}
        tooltipAlignment="center"
        tooltipPosition="bottom"
      />
      <Checkbox
        checked={expandWithCalculationDetails}
        id={'expand-with-calculation-details'}
        labelText={'Calc. Details'}
        onChange={(_evt, { checked }: { checked: boolean }) => {
          setExpandWithCalculationDetails(checked);
        }}
      ></Checkbox>
    </>
  );
};

export { RunsheetSearch };
